import type {
  AnalysisStatusOut,
  AnalysisSummaryOut,
  DocumentPackageOut,
  DocumentPackageUpdate,
  DocumentSummary,
  OutputLanguage,
  ReportOut,
  RetryAnalysisOut,
  ReviewSubmitRequest,
  RowPayload,
  UserOut,
} from "./types";

const BASE = "/api/v1";

class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(detail);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? detail;
    } catch {
      // body not JSON
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // ---- Auth
  login: (email: string, password: string) =>
    request<UserOut>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  me: () => request<UserOut>("/auth/me"),
  models: () => request<Record<string, string>>("/models"),

  // ---- Documents
  listDocuments: () => request<DocumentSummary[]>("/documents"),
  getDocument: (id: string) => request<DocumentSummary>(`/documents/${id}`),
  getDocumentPackage: (id: string) =>
    request<DocumentPackageOut>(`/documents/${id}/package`),
  updateDocumentPackage: (id: string, body: DocumentPackageUpdate) =>
    request<DocumentPackageOut>(`/documents/${id}/package`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  listPages: (id: string) =>
    request<
      Array<{
        page_number: number;
        has_text: boolean;
        char_count: number;
        preview: string;
      }>
    >(`/documents/${id}/pages`),
  uploadDocument: async (files: File[], projectName: string): Promise<DocumentSummary> => {
    const fd = new FormData();
    for (const file of files) fd.append("file", file);
    if (projectName.trim()) fd.append("project_name", projectName.trim());
    const res = await fetch(`${BASE}/documents`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return (await res.json()) as DocumentSummary;
  },

  // ---- Analyses
  startAnalysis: (documentId: string) =>
    request<{ id: string; status: string; resumed: boolean }>(
      `/analyses/documents/${documentId}`,
      {
        method: "POST",
        body: JSON.stringify({ rubric_slug: "lease_v1" }),
      },
    ),
  latestAnalysis: (documentId: string) =>
    request<AnalysisSummaryOut>(`/analyses/documents/${documentId}/latest`),
  analysisStatus: (id: string) =>
    request<AnalysisStatusOut>(`/analyses/${id}/status`),
  retryAnalysis: (id: string) =>
    request<RetryAnalysisOut>(`/analyses/${id}/retry`, { method: "POST" }),
  // `lang` is the view language; ADR 0008 translates when it differs from
  // the analysis language, and ignores it otherwise.
  getRow: (analysisId: string, rowKey: string, lang?: OutputLanguage) =>
    request<RowPayload>(`/analyses/${analysisId}/rows/${rowKey}${lang ? `?lang=${lang}` : ""}`),
  reanalyzeRow: (
    analysisId: string,
    rowKey: string,
    comment: string,
    redo: string[] = ["A", "B", "arbiter"],
  ) =>
    request<{ comment_id: string; enqueued: string[] }>(
      `/analyses/${analysisId}/rows/${rowKey}/reanalyze`,
      {
        method: "POST",
        body: JSON.stringify({ comment, redo }),
      },
    ),
  submitReview: (
    analysisId: string,
    rowKey: string,
    body: ReviewSubmitRequest,
  ) =>
    request<{ id: string }>(
      `/analyses/${analysisId}/rows/${rowKey}/review`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  getReport: (analysisId: string, lang?: OutputLanguage) =>
    request<ReportOut>(`/analyses/${analysisId}/report${lang ? `?lang=${lang}` : ""}`),
  finalize: (analysisId: string) =>
    request<{ ok: true; finalized_at: string }>(`/analyses/${analysisId}/finalize`, {
      method: "POST",
    }),
};

export { ApiError };
